export type SaveFailureReason = 'no-session' | 'unauthorized' | 'network' | 'timeout' | 'server';

export type SaveFailure = {
  ok: false;
  reason: SaveFailureReason;
  message: string;
  status?: number;
  detail?: string;
};

const SIGN_OUT_MESSAGE = "Couldn't save — sign out and back in";

export const NO_SESSION_FAILURE: SaveFailure = {
  ok: false,
  reason: 'no-session',
  message: SIGN_OUT_MESSAGE,
};

export function failureForStatus(status: number | undefined, detail = ''): SaveFailure {
  if (status === 401 || status === 403) {
    return { ok: false, reason: 'unauthorized', message: SIGN_OUT_MESSAGE, status, detail };
  }
  const code = status ? ` (error ${status})` : '';
  return {
    ok: false,
    reason: 'server',
    message: `Maestro couldn't save your preferences${code}. Try again in a moment.`,
    status,
    detail,
  };
}

export function failureForError(err: unknown): SaveFailure {
  const aborted = (err as { name?: string } | null)?.name === 'AbortError';
  if (aborted) {
    return {
      ok: false,
      reason: 'timeout',
      message: 'Saving took too long. Check your connection and try again.',
      detail: 'timeout',
    };
  }
  return {
    ok: false,
    reason: 'network',
    message: "Couldn't reach Maestro — check your internet connection and try again.",
    detail: String((err as { message?: string } | null)?.message ?? err),
  };
}

export type SaveAttemptRequest = {
  fetchImpl: typeof fetch;
  url: string;
  token: string;
  body: Record<string, unknown>;
  timeoutMs: number;
};

export const REQUEST_TIMEOUT_MS = 20_000;

/** One POST with a hard timeout; every outcome is returned, never thrown. fetchImpl is called plain — `this === req` makes WebKit reject at once on macOS 26+ (#11472). */
export async function postOnboardingAttempt(req: SaveAttemptRequest): Promise<{ ok: true } | SaveFailure> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), req.timeoutMs);
  try {
    const resp = await (0, req.fetchImpl)(req.url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${req.token}` },
      body: JSON.stringify(req.body),
      signal: controller.signal,
    });
    if (resp.ok) return { ok: true };
    const detail = await resp.text().catch(() => '');
    return failureForStatus(resp.status, detail);
  } catch (err) {
    return failureForError(err);
  } finally {
    clearTimeout(timer);
  }
}
